import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './css/Sidebar.css';
import logo from '../../assets/logo.png';

const SuperAdminMenu = [
  { text: 'Dashboard', icon: 'fa-tachometer-alt', path: '/super-admin/dashboard' },
  { text: 'Websites', icon: 'fa-globe', path: '/super-admin/websites' },
  { text: 'Admins', icon: 'fa-users-cog', path: '/super-admin/admins' },
  { text: 'Analytics', icon: 'fa-chart-line', path: '/super-admin/analytics' },
];

const SiteAdminMenu = [
  { text: 'Dashboard', icon: 'fa-tachometer-alt', path: '/site-admin/dashboard' },
  { text: 'Posts', icon: 'fa-newspaper', path: '/site-admin/posts' },
  { text: 'Events', icon: 'fa-calendar-alt', path: '/site-admin/events' },
  { text: 'Media', icon: 'fa-photo-video', path: '/site-admin/media' },
  { text: 'Settings', icon: 'fa-cogs', path: '/site-admin/settings' },
];

const UserMenu = [
  { text: 'Home', icon: 'fa-home', path: '/' },
  { text: 'About', icon: 'fa-info-circle', path: '/about' },
];

const Sidebar = ({ onToggle, mobileOpen }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const getMenuItems = () => {
    if (!user) return UserMenu;

    // Strict role-based menu assignment
    switch (user.role) {
      case 'super_admin':
        return SuperAdminMenu;
      case 'site_admin':
        return SiteAdminMenu;
      default:
        return UserMenu;
    }
  };

  const menuItems = getMenuItems();

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onToggle(newState);
  };

const handleMenuItemClick = (path) => {
  // Prevent navigation to unauthorized routes
  if (user?.role === 'site_admin' && path.startsWith('/super-admin')) {
    // Optionally show a toast or alert here
    console.warn('Access denied: Site admin cannot access super admin routes');
    return;
  }
  
  if (user?.role === 'super_admin' && path.startsWith('/site-admin')) {
    // Optionally show a toast or alert here
    console.warn('Access denied: Super admin cannot access site admin routes');
    return;
  }

  navigate(path);
  
  // Close sidebar on mobile after navigation
  if (window.innerWidth <= 768) {
    onToggle(false);
  }
};

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSiteLinkClick = () => {
    if (user && user.siteUrl) {
      window.open(user.siteUrl, '_blank');
    }
  };

  const isActiveItem = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      {/* Logo Section */}
      <div className="sidebar-logo">
        <div className="logo-content">
          <img src={logo} alt="SJ Softwares Logo" className="logo-img" />
          {!isCollapsed && (
            <div className="logo-text">
              <h1>SJ-CMS</h1>
              <span>Content Management</span>
            </div>
          )}
        </div>
        <button className="toggle-btn" onClick={toggleSidebar}>
          <i className={`fas ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
        </button>
      </div>

      {/* Navigation Menu */}
      <ul className="nav-menu">
        {menuItems.map((item, index) => (
          <li
            key={index}
            className={`nav-item ${isActiveItem(item.path) ? 'active' : ''}`}
            onClick={() => handleMenuItemClick(item.path)}
          >
            <i className={`fas ${item.icon}`}></i>
            {!isCollapsed && <span>{item.text}</span>}
            {isActiveItem(item.path) && <div className="active-indicator"></div>}
          </li>
        ))}

        {/* Site Link Button - Only for Site Admins */}
        {user && user.role === 'site_admin' && user.siteUrl && (
          <li
            className="nav-item site-link-item"
            onClick={handleSiteLinkClick}
          >
            <i className="fas fa-external-link-alt"></i>
            {!isCollapsed && <span>Visit Website</span>}
            <div className="site-link-badge">
              <i className="fas fa-rocket"></i>
            </div>
          </li>
        )}

        {/* Logout Button */}
        {user && (
          <li
            className="nav-item logout-item"
            onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt"></i>
            {!isCollapsed && <span>Logout</span>}
          </li>
        )}
      </ul>

      {/* Sidebar Footer */}
      {!isCollapsed && (
        <div className="sidebar-footer">
          <div className="footer-content">
            <p>SJ Softwares & Consultancy</p>
            <span>v1.0.0</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;